import { useState } from 'react';
import { VerticalTimelineElement } from 'react-vertical-timeline-component';
import { format, formatDistanceToNow } from 'date-fns';
import { ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ActivityLog } from './types';
import {
  detectSpecialAction,
  getActionConfig,
  getEntityConfig,
  generateDescription,
  getChangedFields,
  formatFieldName,
  formatValue,
} from './activity-utils';

interface ActivityTimelineItemProps {
  log: ActivityLog;
  showEntityBadge?: boolean;
}

export function ActivityTimelineItem({ log, showEntityBadge = true }: ActivityTimelineItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const action = detectSpecialAction(log);
  const actionConfig = getActionConfig(action);
  const entityConfig = getEntityConfig(log.entity_type);
  const description = generateDescription(log);
  const changes = getChangedFields(log);
  const hasChanges = changes.length > 0 || log.action === 'create';

  const Icon = actionConfig.icon;
  const EntityIcon = entityConfig.icon;

  const actorInitials = log.app_users?.full_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase() || 'S';

  return (
    <VerticalTimelineElement
      className="vertical-timeline-element"
      contentStyle={{
        background: 'hsl(var(--card))',
        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        borderRadius: '0.75rem',
        border: '1px solid hsl(var(--border))',
        padding: '1rem',
      }}
      contentArrowStyle={{ borderRight: '7px solid hsl(var(--border))' }}
      date={formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
      iconStyle={{
        background: 'hsl(var(--background))',
        boxShadow: '0 0 0 4px hsl(var(--border))',
      }}
      icon={<Icon className={`h-4 w-4 ${actionConfig.color}`} />}
    >
      <div className="space-y-2">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Avatar className="h-6 w-6">
              <AvatarImage src={log.app_users?.avatar_url || undefined} />
              <AvatarFallback className="text-xs bg-muted">
                {actorInitials}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium text-foreground">
              {log.app_users?.full_name || 'System'}
            </span>
            {showEntityBadge && (
              <Badge variant="outline" className="text-xs gap-1">
                <EntityIcon className="h-3 w-3" />
                {entityConfig.label}
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {format(new Date(log.created_at), 'MMM d, h:mm a')}
          </span>
        </div>

        {/* Description */}
        <p className="text-sm text-foreground">{description}</p>

        {/* Changes (expandable) */}
        {hasChanges && (
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    Hide details
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3 mr-1" />
                    {log.action === 'create' ? 'View details' : `View ${changes.length} change${changes.length !== 1 ? 's' : ''}`}
                  </>
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                {log.action === 'create' && log.new_value ? (
                  // Show created data
                  <div className="space-y-1">
                    {Object.entries(log.new_value)
                      .filter(([key]) => !['id', 'created_at', 'updated_at'].includes(key))
                      .slice(0, 10)
                      .map(([key, value]) => (
                        <div key={key} className="flex items-start gap-2 text-xs">
                          <span className="font-medium text-muted-foreground min-w-[100px]">
                            {formatFieldName(key)}:
                          </span>
                          <span className="text-foreground break-all">
                            {formatValue(value)}
                          </span>
                        </div>
                      ))}
                  </div>
                ) : (
                  // Show changes
                  changes.slice(0, 10).map(({ field, oldVal, newVal }) => (
                    <div key={field} className="text-xs">
                      <span className="font-medium text-muted-foreground">
                        {formatFieldName(field)}
                      </span>
                      <div className="flex items-center gap-2 mt-1 pl-2">
                        <span className="text-red-600 dark:text-red-400 line-through max-w-[120px] truncate">
                          {formatValue(oldVal)}
                        </span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        <span className="text-green-600 dark:text-green-400 max-w-[120px] truncate">
                          {formatValue(newVal)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
                {(log.action === 'create' && Object.keys(log.new_value || {}).length > 12) && (
                  <p className="text-xs text-muted-foreground">
                    ...and more fields
                  </p>
                )}
                {changes.length > 10 && (
                  <p className="text-xs text-muted-foreground">
                    ...and {changes.length - 10} more changes
                  </p>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </VerticalTimelineElement>
  );
}
